import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import { scheduledClosingsApi } from '../../../api/adminApi';
import {
  scheduledClosingId,
  scheduledClosingDescription,
  scheduledClosingDateInputValue,
  formatScheduledClosingDateShort,
  todayLocalISODate,
} from './scheduledClosingHelpers';
import '../Events/EventView.css';

function ScheduledClosingView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [formTitle, setFormTitle] = useState('');
  const [formClosingDate, setFormClosingDate] = useState('');
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadOne = (opts = {}) => {
    const silent = Boolean(opts.silent);
    if (!id) return Promise.resolve();
    if (!silent) setLoading(true);
    setError('');
    return scheduledClosingsApi
      .getOne(id)
      .then((data) => setItem(data))
      .catch((err) => setError(err.userMessage || 'Failed to load scheduled closing'))
      .finally(() => {
        if (!silent) setLoading(false);
      });
  };

  useEffect(() => {
    loadOne();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const openEdit = () => {
    if (!item) return;
    setFormTitle(item.title ?? '');
    setFormClosingDate(scheduledClosingDateInputValue(item) || todayLocalISODate());
    setFormError('');
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setFormError('');
  };

  const handleSave = (e) => {
    e.preventDefault();
    setFormError('');
    const sid = scheduledClosingId(item);
    if (!sid) return;
    if (!formTitle.trim()) {
      setFormError('Title is required.');
      return;
    }
    if (!formClosingDate || !/^\d{4}-\d{2}-\d{2}$/.test(formClosingDate)) {
      setFormError('Closing date is required (use a valid date).');
      return;
    }
    const nextTitle = formTitle.trim();
    const nextDate = formClosingDate;
    const prevTitle = (item.title ?? '').trim();
    const prevDate = scheduledClosingDateInputValue(item);
    if (nextTitle === prevTitle && nextDate === prevDate) {
      setFormError('Change the title or closing date before saving.');
      return;
    }

    setFormSaving(true);
    scheduledClosingsApi
      .update(sid, {
        title: nextTitle,
        closing_date: nextDate,
      })
      .then(() => loadOne({ silent: true }))
      .then(() => closeEdit())
      .catch((err) => setFormError(err.userMessage || 'Failed to save.'))
      .finally(() => setFormSaving(false));
  };

  const handleDelete = () => {
    const sid = scheduledClosingId(item);
    if (!sid) return;
    setDeleting(true);
    scheduledClosingsApi
      .delete(sid)
      .then(() => navigate('/admin/scheduled-closings', { replace: true }))
      .catch((err) => {
        setError(err.userMessage || 'Failed to delete');
        setDeleteOpen(false);
      })
      .finally(() => setDeleting(false));
  };

  const formatDate = (str) => {
    if (!str) return '—';
    try {
      const d = new Date(str);
      return Number.isNaN(d.getTime())
        ? str
        : d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
      return str;
    }
  };

  if (loading) {
    return <div className="ev-view-loading">Loading scheduled closing…</div>;
  }

  if (error && !item) {
    return (
      <div className="ev-view-wrap">
        <div className="ev-view-header">
          <Link to="/admin/scheduled-closings" className="ev-view-back">
            ← Scheduled closings
          </Link>
        </div>
        <div className="ev-view-error" role="alert">
          {error}
        </div>
      </div>
    );
  }

  const sid = scheduledClosingId(item);
  const body = scheduledClosingDescription(item);

  return (
    <div className="ev-view-wrap">
      <div className="ev-view-header">
        <Link to="/admin/scheduled-closings" className="ev-view-back">
          ← Scheduled closings
        </Link>
        <h2 className="ev-view-title">{item?.title || 'Scheduled closing'}</h2>
        <div className="ev-view-actions">
          <button type="button" className="ev-view-btn ev-view-btn-edit" onClick={openEdit} disabled={!sid}>
            Edit
          </button>
          <button
            type="button"
            className="ev-view-btn ev-view-btn-delete-outline"
            onClick={() => setDeleteOpen(true)}
          >
            Delete
          </button>
        </div>
      </div>

      {error && (
        <div className="ev-view-error ev-view-error--inline" role="alert">
          {error}
        </div>
      )}

      <div className="ev-view-card">
        <dl className="ev-view-meta">
          <div className="ev-view-row">
            <dt>Closing date</dt>
            <dd>{formatScheduledClosingDateShort(scheduledClosingDateInputValue(item))}</dd>
          </div>
          <div className="ev-view-row">
            <dt>Created</dt>
            <dd>{formatDate(item?.created_at)}</dd>
          </div>
          {item?.updated_at && item.updated_at !== item.created_at && (
            <div className="ev-view-row">
              <dt>Updated</dt>
              <dd>{formatDate(item.updated_at)}</dd>
            </div>
          )}
          {item?.creator?.email && (
            <div className="ev-view-row">
              <dt>Author</dt>
              <dd>
                {item.creator.name ? `${item.creator.name} · ${item.creator.email}` : item.creator.email}
              </dd>
            </div>
          )}
        </dl>
        {body ? (
          <div className="ev-view-body">
            <h3 className="ev-view-body-heading">Notes</h3>
            <div className="ev-view-body-text">{body}</div>
          </div>
        ) : null}
      </div>

      <Modal
        isOpen={editOpen}
        onRequestClose={() => {
          if (!formSaving) closeEdit();
        }}
        contentLabel="Edit scheduled closing"
        className="ev-view-modal-content"
        overlayClassName="ev-view-modal-overlay"
      >
        <div className="ev-view-modal-inner">
          <h3 id="sc-view-edit-title">Edit scheduled closing</h3>
          <form onSubmit={handleSave}>
            {formError && (
              <div className="ev-view-form-error" role="alert">
                {formError}
              </div>
            )}
            <div className="ev-view-field">
              <label htmlFor="sc-view-title">Title *</label>
              <input
                id="sc-view-title"
                type="text"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                disabled={formSaving}
              />
            </div>
            <div className="ev-view-field">
              <label htmlFor="sc-view-date">Closing date *</label>
              <input
                id="sc-view-date"
                type="date"
                value={formClosingDate}
                onChange={(e) => setFormClosingDate(e.target.value)}
                disabled={formSaving}
              />
            </div>
            <div className="ev-view-modal-actions">
              <button type="button" className="ev-view-modal-cancel" onClick={closeEdit} disabled={formSaving}>
                Cancel
              </button>
              <button type="submit" className="ev-view-btn ev-view-btn-submit" disabled={formSaving}>
                {formSaving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {deleteOpen && (
        <div className="ev-view-delete-modal" role="dialog" aria-modal="true" aria-labelledby="sc-view-del-title">
          <div className="ev-view-delete-backdrop" onClick={() => !deleting && setDeleteOpen(false)} aria-hidden />
          <div className="ev-view-delete-box">
            <h3 id="sc-view-del-title">Delete this scheduled closing?</h3>
            <p>This soft-deletes the closing. This cannot be undone from the admin UI.</p>
            <div className="ev-view-delete-actions">
              <button
                type="button"
                className="ev-view-btn ev-view-btn-muted"
                onClick={() => setDeleteOpen(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button type="button" className="ev-view-btn ev-view-btn-danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ScheduledClosingView;
